/**
 * AdminProducts Page
 * Admin interface for managing Maccaferri products (geogrid and drainage)
 */

import React, { useState } from 'react';
import { Box, Container, Typography, Tabs, Tab, Button, Paper, CircularProgress, Alert, Snackbar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAdminProducts } from './hooks/useAdminProducts';
import { ProductDataGrid } from './components/ProductDataGrid';
import { DrainageDataGrid } from './components/DrainageDataGrid';
import { ProductFilters } from './components/ProductFilters';
import { AddProductModal } from './components/AddProductModal';
import { ProductType } from './types/AdminTypes';

interface TabPanelProps {
    children?: React.ReactNode;
    value: ProductType;
    selected: ProductType;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, selected }) => {
    return (
        <Box role="tabpanel" hidden={value !== selected} sx={{ pt: 2 }}>
            {value === selected && children}
        </Box>
    );
};

export const AdminProducts: React.FC = () => {
    const {
        filteredGeogridProducts,
        filteredDrainageProducts,
        loading,
        error,
        selectedProductType,
        geogridFamilies,
        drainageCategories,
        filters,
        setSelectedProductType,
        setFilters,
        updateGeogridProduct,
        deleteGeogridProduct,
        createGeogridProduct,
        updateDrainageProduct,
        deleteDrainageProduct,
        createDrainageProduct,
        refreshProducts,
        clearError,
    } = useAdminProducts();

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleTabChange = (_event: React.SyntheticEvent, newValue: ProductType) => {
        setSelectedProductType(newValue);
        setFilters({}); // Reset filters when switching tabs
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const showNotification = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleUpdateGeogrid = async (id: string, product: Parameters<typeof updateGeogridProduct>[1]) => {
        try {
            await updateGeogridProduct(id, product);
            showNotification('Product updated successfully', 'success');
        } catch (err) {
            showNotification('Failed to update product', 'error');
            throw err;
        }
    };

    const handleDeleteGeogrid = async (id: string) => {
        try {
            await deleteGeogridProduct(id);
            showNotification('Product deleted successfully', 'success');
        } catch (err) {
            showNotification('Failed to delete product', 'error');
            throw err;
        }
    };

    const handleAddGeogrid = async (product: Parameters<typeof createGeogridProduct>[0]) => {
        try {
            await createGeogridProduct(product);
            showNotification('Product created successfully', 'success');
        } catch (err) {
            showNotification('Failed to create product', 'error');
            throw err;
        }
    };

    const handleUpdateDrainage = async (id: string, product: Parameters<typeof updateDrainageProduct>[1]) => {
        try {
            await updateDrainageProduct(id, product);
            showNotification('Product updated successfully', 'success');
        } catch (err) {
            showNotification('Failed to update product', 'error');
            throw err;
        }
    };

    const handleDeleteDrainage = async (id: string) => {
        try {
            await deleteDrainageProduct(id);
            showNotification('Product deleted successfully', 'success');
        } catch (err) {
            showNotification('Failed to delete product', 'error');
            throw err;
        }
    };

    const handleAddDrainage = async (product: Parameters<typeof createDrainageProduct>[0]) => {
        try {
            await createDrainageProduct(product);
            showNotification('Product created successfully', 'success');
        } catch (err) {
            showNotification('Failed to create product', 'error');
            throw err;
        }
    };

    return (
        <Container maxWidth={false} disableGutters sx={{ px: 2, py: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Product Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshProducts} disabled={loading}>
                        Refresh
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddModalOpen(true)} disabled={loading}>
                        Add Product
                    </Button>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Main Content */}
            <Paper sx={{ p: 1 }}>
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={selectedProductType} onChange={handleTabChange}>
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Geogrid Products
                                    <Typography variant="caption" sx={{ bgcolor: '#004652', color: 'white', px: 1, borderRadius: 1 }}>
                                        {filteredGeogridProducts.length}
                                    </Typography>
                                </Box>
                            }
                            value="geogrid"
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Drainage Products
                                    <Typography variant="caption" sx={{ bgcolor: '#004652', color: 'white', px: 1, borderRadius: 1 }}>
                                        {filteredDrainageProducts.length}
                                    </Typography>
                                </Box>
                            }
                            value="drainage"
                        />
                    </Tabs>
                </Box>

                {/* Filters */}
                <Box sx={{ mt: 2 }}>
                    <ProductFilters filters={filters} onFiltersChange={setFilters} productType={selectedProductType} families={geogridFamilies} categories={drainageCategories} />
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Data Grids */}
                {!loading && (
                    <>
                        <TabPanel value="geogrid" selected={selectedProductType}>
                            <ProductDataGrid products={filteredGeogridProducts} onUpdate={handleUpdateGeogrid} onDelete={handleDeleteGeogrid} loading={loading} />
                        </TabPanel>
                        <TabPanel value="drainage" selected={selectedProductType}>
                            <DrainageDataGrid products={filteredDrainageProducts} onUpdate={handleUpdateDrainage} onDelete={handleDeleteDrainage} loading={loading} />
                        </TabPanel>
                    </>
                )}
            </Paper>

            {/* Add Product Modal */}
            <AddProductModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onAddGeogrid={handleAddGeogrid}
                onAddDrainage={handleAddDrainage}
                productType={selectedProductType}
                families={geogridFamilies}
                loading={loading}
            />

            {/* Snackbar for notifications */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};
